import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import * as API from '../../API';
import { useOrdersForBatch } from './UseOrdersForBatch';

import {
    Button,
    CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    TableFooter,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import type InvoiceBatch from '../../Models/InvoiceBatch';
import type InvoiceBatchError from '../../Models/InvoiceBatchError';
import type InvoiceBatchHistory from '../../Models/InvoiceBatchHistory';
import type InvoiceBatchOrderHistory from '../../Models/InvoiceBatchOrderHistory';
import type InvoicedOrder from '../../Models/InvoicedOrder';
import Styles from './PreviousInvoicingBatches.module.scss';

function PreviousInvoicingBatches(): React.ReactElement {
    const { t } = useTranslation();

    const [batches, setBatches] = useState<InvoiceBatch[]>([]);
    const [totalAmountOfBatches, setTotalAmountOfBatches] = useState(0);
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);

    const getBatches = (): void => {
        setLoading(true);
        API.eFact.getPreviousInvoicingBatches(filter, page, rowsPerPage)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with getPreviousInvoicingBatches:', res);
                }

                setBatches(res.Batches);
                setTotalAmountOfBatches(res.TotalAmount);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Could not get previous invoicing batches:', err);
            });
    };

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(getBatches, [page, rowsPerPage]);

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <SearchOnKeyDownInput getBatches={getBatches} setFilter={setFilter} setPage={setPage} />
            </Grid>
            <Grid size={12}>
                <TableContainer component={Paper}>
                    <Table size="small" id="previousBatchList" className={Styles.rows}>
                        <TableHead>
                            <TableRow>
                                {/* TextAlign left is necessary here to overwrite datatables.css overzealous styling... */}
                                <TableCell className={`center ${Styles.sWidth} ${Styles.bold}`}>{t('Batch')} #</TableCell>
                                <TableCell className={`center ${Styles.sWidth} ${Styles.bold}`}>{t('StartDate')}</TableCell>
                                <TableCell className={`center ${Styles.sWidth} ${Styles.bold}`}>{t('EndDate')}</TableCell>
                                <TableCell className={`center ${Styles.mWidth} ${Styles.bold}`}>{t('Sites')}</TableCell>
                                <TableCell className={`center ${Styles.lWidth} ${Styles.bold}`}>{t('BatchStatusInfo')}</TableCell>
                                <TableCell className={`center ${Styles.sWidth} ${Styles.bold}`}>{t('LastModification')}</TableCell>
                                <TableCell className={`center ${Styles.xsWidth} ${Styles.bold}`}>{t('NumberOfOrders')}</TableCell>
                                <TableCell className={`center ${Styles.xsWidth} ${Styles.bold}`}>{t('ShowAll')}</TableCell>
                                <TableCell className={`center ${Styles.xsWidth} ${Styles.bold}`}>{t('Status')}</TableCell>
                                <TableCell className={`center ${Styles.xsWidth} ${Styles.bold}`}>{t('DeleteAll')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { loading
                                ? (
                                        <TableRow>
                                            <TableCell colSpan={10} sx={{ textAlign: 'center' }}>
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    )
                                : batches.map((x, i) => <BatchRow key={x.ID} batch={x} />) }
                        </TableBody>
                        <TableFooter>
                            <TablePagination
                                style={{ overflow: 'initial' }}
                                component="div"
                                rowsPerPageOptions={[5, 10, 25]}
                                count={totalAmountOfBatches}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
}

function SearchOnKeyDownInput({ setFilter, getBatches, setPage }: Readonly<{ setFilter: Function; getBatches: Function; setPage: Function }>): React.ReactElement {
    const { t } = useTranslation();

    const handleFilter = (
        searchTerm: string,
    ): void => {
        setPage(0);
        setFilter(searchTerm);
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
        if (event.key === 'Enter') {
            getBatches();
        }
    };

    return (
        <TextField className={Styles.searchField} label={t('FilterOn') + ' ' + t('OrderNumber') + ' ' + t('Or') + ' ' + t('Batch')} variant="outlined" onChange={(e) => handleFilter(e.target.value)} size="small" onKeyDown={handleKeyDown} />
    );
}

interface OrderRowsProps {
    orders: InvoicedOrder[];
    hide: boolean;
    isLoading: boolean;
}

function OrderRows({ orders, hide, isLoading }: Readonly<OrderRowsProps>): ReactNode {
    if (hide) {
        return null;
    }
    if (isLoading) {
        return <LoadingRow />;
    }
    return orders.map((x) => <OrderRow key={x.ID} order={x} />);
}

function BatchRow({ batch }: Readonly<{ batch: InvoiceBatch }>): React.ReactElement {
    const [expand, setExpand] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const [errorsDialogOpen, setErrorsDialogOpen] = useState(false);

    const [batchInfoDialogOpen, setBatchInfoDialogOpen] = useState(false);

    const [removeAllOrdersFromBatchDialogOpen, setRemoveAllOrdersFromBatchDialogOpen] = useState(false);

    const contextMenuOpen = Boolean(anchorEl);

    const { orders, loading } = useOrdersForBatch(batch.ID, expand);

    const handleExpand = (): void => {
        setExpand(!expand);
    };

    const handleContextMenu = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>): void => {
        e.preventDefault();
        setAnchorEl(e.currentTarget);
    };

    const handleCloseContextMenu = (): void => {
        setAnchorEl(null);
    };

    const handleOpenErrors = (): void => {
        setErrorsDialogOpen(true);
        handleCloseContextMenu();
    };

    const handleCloseErrors = (): void => {
        setErrorsDialogOpen(false);
    };

    const handleCloseRemoveAllOrdersFromBatch = (): void => {
        setRemoveAllOrdersFromBatchDialogOpen(false);
    };

    const handleSetRemoveAllOrdersFromBatchDialogOpen = (): void => {
        setRemoveAllOrdersFromBatchDialogOpen(true);
    };

    const handleSetBatchInfoDialogOpen = (): void => {
        setBatchInfoDialogOpen(true);
    };

    const handleCloseBatchInfoDialogOpen = (): void => {
        setBatchInfoDialogOpen(false);
    };

    return (
        <>
            <TableRow className={'previousBatch ' + (expand ? 'active' : '')} id={'batch' + batch.ID}>
                <TableCell className={`${Styles.sWidth}`} onContextMenu={handleContextMenu}>{batch.Number}</TableCell>
                <TableCell className={`${Styles.sWidth}`} onContextMenu={handleContextMenu}>{batch.PeriodStart}</TableCell>
                <TableCell className={`${Styles.sWidth}`} onContextMenu={handleContextMenu}>{batch.PeriodEnd}</TableCell>
                <TableCell className={`${Styles.mWidth}`} onContextMenu={handleContextMenu}>{batch.Sites}</TableCell>
                <TableCell className={`${Styles.extraInfo} ${Styles.lWidth}`} onClick={handleSetBatchInfoDialogOpen}>{batch.ExtraInfo}</TableCell>
                <TableCell className={`${Styles.sWidth}`} onContextMenu={handleContextMenu}>{batch.CreatedBy} ({batch.Created})</TableCell>
                <TableCell className={`${Styles.xsWidth}`} onContextMenu={handleContextMenu}>{batch.OrdersAmount}</TableCell>
                <TableCell className={`showAllOrdersInBatch ${Styles.xsWidth}`} onClick={handleExpand}>&nbsp;</TableCell>
                <TableCell className={`${Styles.xsWidth}`} onContextMenu={handleContextMenu}>{batch.Status}</TableCell>
                <TableCell className={`delete ${Styles.xsWidth}`} onClick={handleSetRemoveAllOrdersFromBatchDialogOpen}>&nbsp;</TableCell>
            </TableRow>
            <OrderRows orders={orders} hide={!expand} isLoading={loading} />
            <ErrorMenu anchorEl={anchorEl} contextMenuOpen={contextMenuOpen} handleOpenErrors={handleOpenErrors} handleCloseContextMenu={handleCloseContextMenu} />
            <ErrorsDialog batchID={batch.ID} errorsDialogOpen={errorsDialogOpen} handleCloseErrors={handleCloseErrors} />
            <BatchInfoDialog info={batch.ExtraInfo} batchInfoDialogOpen={batchInfoDialogOpen} handleCloseBatchInfoDialogOpen={handleCloseBatchInfoDialogOpen} />
            <RemoveAllOrdersFromBatchDialog batchID={batch.ID} removeAllOrdersFromBatchDialogOpen={removeAllOrdersFromBatchDialogOpen} handleCloseRemoveAllOrdersFromBatch={handleCloseRemoveAllOrdersFromBatch} />
        </>
    );
}

function RemoveAllOrdersFromBatchDialog({ batchID, removeAllOrdersFromBatchDialogOpen, handleCloseRemoveAllOrdersFromBatch }: Readonly<{
    batchID: number; removeAllOrdersFromBatchDialogOpen: boolean; handleCloseRemoveAllOrdersFromBatch: any;
}>): React.ReactElement {
    const { t } = useTranslation();
    const [removeInvoicesFromAllOrders, setRemoveInvoicesFromAllOrders] = useState(true);

    const handleChangeRemoveInvoicesFromAllOrders = (): void => {
        setRemoveInvoicesFromAllOrders(!removeInvoicesFromAllOrders);
    };

    const handleUnlinkOrdersFromBatch = (): void => {
        API.eFact.unlinkAllOrdersFromBatch(batchID, removeInvoicesFromAllOrders)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with handleUnlinkOrdersFromBatch:', res);
                }
                handleCloseRemoveAllOrdersFromBatch();
            })
            .catch((err) => {
                console.error('Could not unlink orders from invoicing batch:', err);
            });
    };

    return (
        <Dialog
            onClose={handleCloseRemoveAllOrdersFromBatch}
            aria-labelledby="customized-dialog-title"
            open={removeAllOrdersFromBatchDialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('RemoveAllOrdersFromBatch')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseRemoveAllOrdersFromBatch}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <span className="material-symbols-outlined">close</span>
            </IconButton>
            <DialogContent>
                <p className={Styles.remark}>{t('InfoRemoveAllOrdersFromBatch')}</p>
                <p className={Styles.remarkCheckbox}>
                    <input type="checkbox" className="check-box clearfix" checked={removeInvoicesFromAllOrders} onChange={handleChangeRemoveInvoicesFromAllOrders} name="removeInvoicesFromAllOrders" />
                    <label htmlFor="removeInvoicesFromAllOrders">{t('AlsoDeleteConfirmedInvoices')}</label>
                </p>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleUnlinkOrdersFromBatch}>
                    {t('Ok')}
                </Button>
                <Button onClick={handleCloseRemoveAllOrdersFromBatch}>
                    {t('Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BatchInfoDialog({ info, batchInfoDialogOpen, handleCloseBatchInfoDialogOpen }: Readonly<{
    info: string; batchInfoDialogOpen: boolean; handleCloseBatchInfoDialogOpen: any;
}>): React.ReactElement {
    const { t } = useTranslation();

    return (
        <Dialog
            onClose={handleCloseBatchInfoDialogOpen}
            aria-labelledby="customized-dialog-title"
            open={batchInfoDialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('Info')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseBatchInfoDialogOpen}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <span className="material-symbols-outlined">close</span>
            </IconButton>
            <DialogContent>
                <Typography className={Styles.selectable}>{info}</Typography>
            </DialogContent>
        </Dialog>
    );
}

function ErrorsDialog({ batchID, errorsDialogOpen, handleCloseErrors }: Readonly<{
    batchID: number; errorsDialogOpen: boolean; handleCloseErrors: any;
}>): React.ReactElement {
    const { t } = useTranslation();

    const handlePDF = (): void => {
        window.open('/legacy/Invoices/PDFBatchErrors?Batch_UID=' + batchID);
    };

    return (
        <Dialog
            fullWidth={true}
            maxWidth="lg"
            onClose={handleCloseErrors}
            aria-labelledby="customized-dialog-title"
            open={errorsDialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('Errors')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseErrors}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <span className="material-symbols-outlined">close</span>
            </IconButton>
            <DialogContent>
                <ErrorsTable batchID={batchID} />
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handlePDF}>
                    {t('PDF')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ErrorMenu({ anchorEl, contextMenuOpen, handleCloseContextMenu, handleOpenErrors }: Readonly<{ anchorEl: null | HTMLElement; contextMenuOpen: boolean;
    handleCloseContextMenu: any; handleOpenErrors: any; }>): React.ReactElement {
    const { t } = useTranslation();

    return (
        <Menu
            id="demo-positioned-menu"
            aria-labelledby="demo-positioned-button"
            anchorEl={anchorEl}
            open={contextMenuOpen}
            onClose={handleCloseContextMenu}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <MenuItem onClick={handleOpenErrors}>{t('Errors')}</MenuItem>
        </Menu>
    );
}

function LoadingRow(): React.ReactElement {
    return (
        <TableRow>
            <TableCell colSpan={10} sx={{ textAlign: 'center' }}>
                <CircularProgress />
            </TableCell>
        </TableRow>
    );
}

function ErrorsTable({ batchID }: Readonly<{ batchID: number }>): React.ReactElement {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<InvoiceBatchError[]>([]);

    const getErrorsForBatch = (): void => {
        API.eFact.getErrorsForPreviousInvoicingBatch(batchID)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with getOrdersForPreviousInvoicingBatch:', res);
                }
                setErrors(res.Errors);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Could not get orders linked to previous invoicing batch:', err);
            });
    };

    useEffect(getErrorsForBatch, []);

    return (
        <TableContainer component={Paper}>
            <Table size="small" id="batchErrorList">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('PatientID')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('VisitNumber')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('OrderNumber')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('ImplementationDate')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Order') + ' ' + t('Status')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('ErrorMessage')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { loading
                        ? (
                                <TableRow>
                                    <TableCell colSpan={10} sx={{ textAlign: 'center' }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            )
                        : errors.map((x, i) => <ErrorRow key="" error={x} />) }
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ErrorRow({ error }: Readonly<{ error: InvoiceBatchError }>): React.ReactElement {
    return (
        <TableRow className="errorInfo">
            <TableCell className="center pr8">{error.PatientID}</TableCell>
            <TableCell className="center pr8">{error.VisitNumber}</TableCell>
            <TableCell className="center pr8">{error.OrderNumber}</TableCell>
            <TableCell className="center pr8">{error.OrderDateTime}</TableCell>
            <TableCell className="center pr8">{error.OrderStatus}</TableCell>
            <TableCell className="center pr20 extraInfo">{error.ErrorMessage}</TableCell>
        </TableRow>
    );
}

function BatchStatusCellContent({ order }: Readonly<{ order: InvoicedOrder }>): ReactElement {
    let color = '';
    if (order.BatchStatus === 1) {
        color = '#ffa500';
    }
    if (order.BatchStatus === 2) {
        color = '#008800';
    }
    if (order.BatchStatus === 3) {
        color = '#cd0a0a';
    }

    return <span style={{ color: color }}>{order.BatchStatusValue}</span>;
}

function OrderRow({ order }: Readonly<{ order: InvoicedOrder }>): React.ReactElement {
    const { t } = useTranslation();
    const [orderBatchHistoryDialogOpen, setOrderBatchHistoryDialogOpen] = useState(false);
    const [removeOrderFromBatchDialogOpen, setRemoveOrderFromBatchDialogOpen] = useState(false);
    const [removeInvoiceFromOrder, setRemoveInvoiceFromOrder] = useState(true);

    const handleOrderBatchHistoryDialogOpen = (): void => {
        setOrderBatchHistoryDialogOpen(true);
    };
    const handleCloseOrderBatchHistoryDialog = (): void => {
        setOrderBatchHistoryDialogOpen(false);
    };

    const handleRemoveOrderFromBatchDialogOpen = (): void => {
        setRemoveOrderFromBatchDialogOpen(true);
    };

    const handleCloseRemoveOrderFromBatchDialog = (): void => {
        setRemoveOrderFromBatchDialogOpen(false);
    };

    const handleChangeRemoveInvoiceFromOrder = (): void => {
        setRemoveInvoiceFromOrder(!removeInvoiceFromOrder);
    };

    const handleRemoveInvoiceFromOrder = (): void => {
        API.eFact.unlinkOrderFromBatch(order.ID, removeInvoiceFromOrder)
            .then((res: { AllOk: boolean }) => {
                if (!res.AllOk) {
                    return console.error('Issue with handleUnlinkOrdersFromBatch:', res);
                }
                handleCloseRemoveOrderFromBatchDialog();
            })
            .catch((err) => {
                console.error('Could not unlink orders from invoicing batch:', err);
            });
    };

    return (
        <>
            <TableRow id={'orderInBatch' + order.ID}>
                <TableCell className="center sWidth">{order.Implementation_DateFormat}</TableCell>
                <TableCell className="center sWidth">{order.OrderStatus}</TableCell>
                <TableCell className="lWidth">{order.PatientName}</TableCell>
                <TableCell className="center sWidth">{order.InvoiceStatus}</TableCell>
                <TableCell className="center sWidth">
                    <BatchStatusCellContent order={order} />
                </TableCell>
                <TableCell className="center sWidth batchHistory" onClick={handleOrderBatchHistoryDialogOpen}>{t('History')}</TableCell>
                <TableCell className="center sWidth linkOrder">
                    <span># </span>
                    <span className="orderNumber">{order.OrderNumber}</span>
                    <span className="hidden orderID">{order.ID}</span>
                </TableCell>
                <TableCell className="center xsWidth delete" onClick={handleRemoveOrderFromBatchDialogOpen}>&nbsp;</TableCell>
            </TableRow>
            <OrderBatchHistoryDialog orderBatchHistoryDialogOpen={orderBatchHistoryDialogOpen} handleCloseOrderBatchHistoryDialog={handleCloseOrderBatchHistoryDialog} orderID={order.ID} />
            <RemoveErrorDialog handleCloseRemoveOrderFromBatchDialog={handleCloseRemoveOrderFromBatchDialog} handleChangeRemoveInvoiceFromOrder={handleChangeRemoveInvoiceFromOrder} handleRemoveInvoiceFromOrder={handleRemoveInvoiceFromOrder} removeOrderFromBatchDialogOpen={removeOrderFromBatchDialogOpen} removeInvoiceFromOrder={removeInvoiceFromOrder} />
        </>
    );
}

function OrderBatchHistoryDialog({ orderBatchHistoryDialogOpen, handleCloseOrderBatchHistoryDialog, orderID }: Readonly<{
    orderBatchHistoryDialogOpen: boolean; handleCloseOrderBatchHistoryDialog: any; orderID: number;
}>): React.ReactElement {
    const { t } = useTranslation();

    return (
        <Dialog
            fullWidth={true}
            maxWidth="lg"
            onClose={handleCloseOrderBatchHistoryDialog}
            aria-labelledby="customized-dialog-title"
            open={orderBatchHistoryDialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('BatchHistory')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseOrderBatchHistoryDialog}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <span className="material-symbols-outlined">close</span>
            </IconButton>
            <DialogContent>
                <HistoryTable orderID={orderID} />
            </DialogContent>
        </Dialog>
    );
}

function HistoryTable({ orderID }: Readonly<{ orderID: number }>): React.ReactElement {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [batchHistory, setBatchHistory] = useState<InvoiceBatchHistory>();

    const getErrorsForBatch = (): void => {
        API.eFact.getInvoiceBatchHistoryOfOrder(orderID)
            .then((res) => {
                if (!res.AllOk) {
                    return console.error('Issue with getInvoiceBatchHistoryOfOrder:', res);
                }
                setBatchHistory(res.History);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Could not get history:', err);
            });
    };

    useEffect(getErrorsForBatch, []);

    return (
        <>
            <div className="specifications">
                <p>
                    {t('Order')}
                    <span id="orderBatchHistoryNumber">#{batchHistory?.OrderNumber}</span>
                </p>
                <p>
                    {t('Implementation')}
                    <span id="orderBatchHistoryImplementation">{batchHistory?.Order_Implementation}</span>
                </p>
                <p>
                    {t('Patient')}
                    <span id="orderBatchHistoryPatient">{batchHistory?.PatientName}</span>
                </p>
            </div>
            <TableContainer component={Paper}>
                <Table size="small" id="batchErrorList">
                    <TableHead>
                        <TableRow>
                            <TableCell className={`center ${Styles.bold} ${Styles.mWidth}`}>{t('Status')}</TableCell>
                            <TableCell className={`center ${Styles.bold} ${Styles.sWidth}`}>{t('Batch')}#</TableCell>
                            <TableCell className={`center ${Styles.bold} ${Styles.lWidth}`}>{t('StartDate')}</TableCell>
                            <TableCell className={`center ${Styles.bold} ${Styles.lWidth}`}>{t('EndDate')}</TableCell>
                            <TableCell className={`center ${Styles.bold} ${Styles.xlWidth}`}>{t('LastModification')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        { loading
                            ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center' }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                )
                            : batchHistory?.List.map((x, i) => <HistoryRow key={x.ID} historyItem={x} />) }
                    </TableBody>
                </Table>
            </TableContainer>
        </>

    );
}

function HistoryRow({ historyItem }: Readonly<{ historyItem: InvoiceBatchOrderHistory }>): React.ReactElement {
    return (
        <TableRow>
            <TableCell className={`center ${Styles.mWidth}`}>{historyItem.StatusValue}</TableCell>
            <TableCell className={`center ${Styles.sWidth}`}>{historyItem.InvoiceBatch_Number}</TableCell>
            <TableCell className={`center ${Styles.lWidth}`}>{historyItem.DateTime_BatchPeriodStart}</TableCell>
            <TableCell className={`center ${Styles.lWidth}`}>{historyItem.DateTime_BatchPeriodEnd}</TableCell>
            <TableCell className={`center ${Styles.xlWidth}`}>{historyItem.User_UserFormat} ({historyItem.DateTime_DateFormat})</TableCell>
        </TableRow>
    );
}

function RemoveErrorDialog({ handleCloseRemoveOrderFromBatchDialog, handleChangeRemoveInvoiceFromOrder, handleRemoveInvoiceFromOrder, removeOrderFromBatchDialogOpen, removeInvoiceFromOrder }: Readonly<{
    handleCloseRemoveOrderFromBatchDialog: any; handleChangeRemoveInvoiceFromOrder: any; handleRemoveInvoiceFromOrder: any; removeOrderFromBatchDialogOpen: boolean; removeInvoiceFromOrder: boolean;
}>): React.ReactElement {
    const { t } = useTranslation();

    return (
        <Dialog
            onClose={handleCloseRemoveOrderFromBatchDialog}
            aria-labelledby="customized-dialog-title"
            open={removeOrderFromBatchDialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('RemoveOrderFromBatch')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseRemoveOrderFromBatchDialog}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <span className="material-symbols-outlined">close</span>
            </IconButton>
            <DialogContent>
                <p className={Styles.remark}>{t('InfoRemoveOrderFromBatch')}</p>
                <p className={Styles.remarkCheckbox}>
                    <input type="checkbox" checked={removeInvoiceFromOrder} onChange={handleChangeRemoveInvoiceFromOrder} name="removeInvoicesOrder" />
                    <label htmlFor="removeInvoicesOrder">{t('AlsoDeleteConfirmedInvoice')}</label>
                </p>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleRemoveInvoiceFromOrder}>
                    {t('Ok')}
                </Button>
                <Button onClick={handleCloseRemoveOrderFromBatchDialog}>
                    {t('Cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PreviousInvoicingBatches;
